import { type ShopperCustomersTypes } from 'commerce-sdk-isomorphic'
import { defineStore } from 'pinia'
import { VSF_MHJ_MY_STORE_ID, VSF_MHJ_MY_STORE_NAME, VSF_MHJ_MY_STORE_SEARCH_TERM } from '~/config/cookie'

interface GuestUserPayload {
  customer_id: string
  usid: string
}

export type GuestUser = {
  customerId: string
  usId: string
}

const LOGOUT_REQUEST_TIMEOUT_MS = 1500 // wait time for the SLAS logoutCustomer call before navigating away

function guardedCleanupStep(label: string, step: () => void) {
  try {
    step()
  } catch (e) {
    console.error(`logout: failed to ${label}`, e)
  }
}

export const useUserStore = defineStore('user', () => {
  const user = ref<ShopperCustomersTypes.Customer | null>(null)
  const guestUser = ref<GuestUser | null>(null)

  const accessTokenCookie = useCookie('access_token')
  // At setup as useCookie() can throw "[nuxt] instance unavailable" if invoked during a reactive flush outside a valid Nuxt context (e.g. SSR).
  const myStoreIdCookie = useCookie(VSF_MHJ_MY_STORE_ID)
  const myStoreNameCookie = useCookie(VSF_MHJ_MY_STORE_NAME)

  // Getters as computed
  //TODO: Can be a single computed property as they are all dependent on user.value
  const isLoggedIn = computed(
    // fallback to using the access token to see if user is logged in, this will only be used in a server context to ensure
    // personalized member content is rendered correctly from ssr and not causing hydration issues when user is loaded later in client
    () => Boolean(user.value?.customerId) || isRegisteredToken(accessTokenCookie.value),
  )
  const firstName = computed(() => user.value?.firstName ?? '')
  const initials = computed(() => `${user.value?.firstName?.[0] ?? ''}${user.value?.lastName?.[0] ?? ''}`.toUpperCase())
  const fullName = computed(() => `${user.value?.firstName ?? ''} ${user.value?.lastName ?? ''}`)
  const myStoreId = computed(() => user.value?.c_preferredStore?.split(' - ')[0])
  const myStoreName = computed(() => user.value?.c_preferredStore?.split(' - ')[1])

  // Sync cookies when store values change
  watch(
    [myStoreId, myStoreName],
    ([storeId, storeName]) => {
      if (storeId) myStoreIdCookie.value = storeId
      if (storeName) myStoreNameCookie.value = storeName
    },
    { immediate: true },
  )

  // Actions
  function setUser(userData: ShopperCustomersTypes.Customer | null) {
    if (userData?.customerId !== user.value?.customerId) resetStoreSaves()
    user.value = userData
  }

  function setGuestUser(guest: GuestUserPayload | null): void {
    guestUser.value = { customerId: guest?.customer_id ?? '', usId: guest?.usid ?? '' }
  }

  // Saves are numbered so an older response landing late can't undo the shopper's latest choice and
  // rewrite the store cookies. The newest accepted save is only applied once none are in flight.
  let storeSaveSession = 0
  let lastSentStoreSave = 0
  let lastConfirmedStoreSave = 0
  let storeSavesInFlight = 0
  let confirmedUser: ShopperCustomersTypes.Customer | null = null

  // A different account (or none) makes any save still in flight belong to someone else.
  function resetStoreSaves() {
    storeSaveSession++
    storeSavesInFlight = 0
    confirmedUser = null
  }

  /** Resolves false only when this is the latest save and it failed. */
  async function savePreferredStore(preferredStore: string): Promise<boolean> {
    const customerId = user.value?.customerId
    if (!customerId) return true
    const session = storeSaveSession
    const save = ++lastSentStoreSave
    storeSavesInFlight++
    const newUserValue = await useRegisteredCustomer().updateCustomer(
      { c_preferredStore: preferredStore },
      { customerId },
    )
    if (session !== storeSaveSession || user.value?.customerId !== customerId) return true
    storeSavesInFlight--
    if (newUserValue && save > lastConfirmedStoreSave) {
      lastConfirmedStoreSave = save
      confirmedUser = newUserValue
    }
    if (!storeSavesInFlight && confirmedUser) {
      user.value = confirmedUser
      confirmedUser = null
    }
    return save !== lastSentStoreSave || !!newUserValue
  }

  function setMyStore(store: CustomerSetMyStoreSchema) {
    return savePreferredStore(`${store.id} - ${store.name}`)
  }

  function clearMyStore() {
    return savePreferredStore('')
  }

  function reset() {
    resetStoreSaves()
    user.value = null
    guestUser.value = null
  }

  async function logout() {
    // Bump before any other work: a token refresh (useRefreshTokens/useLogin) that is mid-flight will re-check this generation after its own await(s)
    // and bail out rather than write a freshly rotated auth cookie / setUser() over the anonymous state this call is about to establish.
    const authGeneration = useState<number>('_auth_generation', () => 0)
    authGeneration.value++

    const refreshToken = useCookie('refresh_token').value

    // Security-critical: clear the state that actually gates isLoggedIn/API access first so nothing below can pre-empt it.
    reset()
    // Null accessTokenCookie directly rather than relying on the async cookie-change notification by clearCookies()
    accessTokenCookie.value = null
    clearCookies([
      'access_token',
      'refresh_token',
      VSF_MHJ_MY_STORE_SEARCH_TERM,
      VSF_MHJ_MY_STORE_ID,
      VSF_MHJ_MY_STORE_NAME,
    ])

    try {
      // Best-effort cleanups: each step is independently guarded
      guardedCleanupStep('reset shop store', () => useShopStore().reset())
      guardedCleanupStep('reset wishlist store', () => useWishlistStore().reset())
      guardedCleanupStep('reset baskets store', () => useBasketsStore().reset())
      guardedCleanupStep('sync Braze logged-in status', () => useBrazeTracking().syncLoggedInStatus(false))
      guardedCleanupStep('clear persisted Braze CID', () => useBrazeIdentity().clearPersistedCid())
      guardedCleanupStep('clear recent searches', () => useRecentSearches().clear())
      guardedCleanupStep('clear search attribution', () => useSearchAttribution().clearSearchAttribution())

      if (import.meta.client) {
        // sessionStorage throws in Safari private mode - clear each key independently so one failure doesn't prevent the others being cleared.
        ;[
          'billingPostcode',
          'adyenPaymentMethod',
          // Barilliance "Drop a Hint" PII (config/barilliance-drop-a-hint.js). That script is configured in the Barilliance dashboard and
          // runs outside our bundle, so we clear its sessionStorage keys here instead of in the script itself.
          'cbar_data_uinf[dah_recipientName]',
          'cbar_data_uinf[dah_recipientEmail]',
          'cbar_data_uinf[dah_yourName]',
          'cbar_data_uinf[dah_yourEmail]',
        ].forEach((key) => guardedCleanupStep(`clear ${key} from sessionStorage`, () => sessionStorage.removeItem(key)))

        // Reset Heap's associated SFCC customer ID so subsequent activity on a shared device isn't attributed to this customer.
        guardedCleanupStep('reset Heap identity', () => window.heap?.resetIdentity?.())
      }

      // NIST - Invalidate the refresh token, otherwise it remains usable until expiry (up to 90 days) on a shared/compromised device.
      if (refreshToken) {
        let timeoutHandle: ReturnType<typeof setTimeout> | undefined
        const logoutCustomer = Promise.resolve()
          .then(() =>
            useShopper().shopperLogin.logoutCustomer({
              parameters: { refresh_token: refreshToken, client_id: useRuntimeConfig().public.SFCC_CLIENT_ID },
            }),
          )
          .catch((e) => {
            console.warn('Failed to invalidate refresh token on logout:', e instanceof Error ? e.message : String(e))
          })
        const timeout = new Promise((resolve) => {
          timeoutHandle = setTimeout(resolve, LOGOUT_REQUEST_TIMEOUT_MS)
        })
        try {
          // Deliberately await (raced against a timeout) as navigate away immediately can abort the in-flight request before SLAS receives it.
          await Promise.race([logoutCustomer, timeout])
        } finally {
          clearTimeout(timeoutHandle)
        }
      }
    } finally {
      if (import.meta.client) {
        try {
          // useCookie() only queues its document.cookie write via a Vue watcher (nuxt's cookie.js), not synchronously.
          // Wait to flush cookies cleared above before navigation, otherwise they can exist for an instant after redirecting.
          await nextTick()
        } catch (e) {
          console.error('logout: failed to await pending cookie writes before navigating', e)
        }
        window.location.assign(`${useSiteUrl().getLocalePrefix()}/my-account`)
      }
    }
  }

  return {
    user,
    guestUser,
    isLoggedIn,
    firstName,
    initials,
    fullName,
    myStoreId,
    myStoreName,
    setUser,
    setGuestUser,
    setMyStore,
    clearMyStore,
    logout,
  }
})
